import { useState } from "react";
import { Table, Button, Modal, Form, Select, DatePicker, InputNumber, Popconfirm, message, Space } from "antd";
import { Plus } from "lucide-react";
import dayjs from "dayjs";
import { useHRUsers, useStatutory, useStatutoryCreate, useStatutoryUpdate, useStatutoryDelete } from "../../../../api-hooks/hr";

const PAY_TYPES = [
  { value: "ssp", label: "SSP — Statutory Sick Pay" },
  { value: "smp", label: "SMP — Statutory Maternity Pay" },
  { value: "spp", label: "SPP — Statutory Paternity Pay" },
  { value: "sap", label: "SAP — Statutory Adoption Pay" },
  { value: "spbp", label: "SPBP — Shared Parental Pay" },
];

export default function StatutoryTab({ cid }: { cid: number }) {
  const [msgApi, ctx] = message.useMessage();
  const [form] = Form.useForm();
  const [modal, setModal] = useState<{ open: boolean; rec: any }>({ open: false, rec: null });

  const { data: users = [] } = useHRUsers(cid);
  const { data: rows = [], isLoading } = useStatutory(cid);

  const cb = (msg: string, fn?: () => void) => ({ onSuccess: () => { msgApi.success(msg); fn?.(); }, onError: (e: any) => msgApi.error(e?.message || "Error") });
  const create = useStatutoryCreate(cid, cb("Created.", () => setModal({ open: false, rec: null })));
  const upd = useStatutoryUpdate(cid, cb("Updated.", () => setModal({ open: false, rec: null })));
  const del = useStatutoryDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });

  const cols = [
    { title: "Employee", dataIndex: "user_name", key: "u" },
    { title: "Type", dataIndex: "payment_type", key: "pt", render: (v: string) => v?.toUpperCase() },
    { title: "Start", dataIndex: "start_date", key: "s", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "End", dataIndex: "end_date", key: "e", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Weekly Rate", dataIndex: "weekly_rate", key: "wr", render: (v: number) => v ? `£${v}` : "—" },
    { title: "Total Paid", dataIndex: "total_paid", key: "tp", render: (v: number) => `£${v ?? 0}` },
    { title: "Weeks", dataIndex: "weeks_paid", key: "wp" },
    { title: "", key: "act", render: (_: any, r: any) => (
      <Space size="small">
        <Button size="small" onClick={() => { setModal({ open: true, rec: r }); form.setFieldsValue({ ...r, start_date: r.start_date ? dayjs(r.start_date) : null, end_date: r.end_date ? dayjs(r.end_date) : null }); }}>Edit</Button>
        <Popconfirm title="Delete?" onConfirm={() => del.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm>
      </Space>
    )},
  ];

  return (
    <div>
      {ctx}
      <div className="flex justify-end mb-3">
        <Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => { setModal({ open: true, rec: null }); form.resetFields(); form.setFieldsValue({ payment_type: "ssp", total_paid: 0, weeks_paid: 0 }); }} style={{ background: "#4c1d95" }}>Add Record</Button>
      </div>
      <Table rowKey="id" dataSource={rows as any[]} columns={cols} loading={isLoading} size="small" pagination={false} />
      <Modal open={modal.open} onCancel={() => setModal({ open: false, rec: null })} footer={null} destroyOnClose title={modal.rec ? "Edit Statutory Payment" : "New Statutory Payment"} width={480}>
        <Form form={form} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => {
          const p = { ...vals, start_date: vals.start_date?.format("YYYY-MM-DD"), end_date: vals.end_date?.format("YYYY-MM-DD") };
          if (modal.rec) upd.mutate({ id: modal.rec.id, ...p }); else create.mutate(p);
        }}>
          <Form.Item name="user_id" label="Employee" rules={[{ required: true }]}><Select options={(users as any[])?.map(u => ({ value: u.id, label: u.name }))} showSearch optionFilterProp="label" /></Form.Item>
          <Form.Item name="payment_type" label="Payment Type" rules={[{ required: true }]}><Select options={PAY_TYPES} /></Form.Item>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="start_date" label="Start Date" rules={[{ required: true }]}><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
            <Form.Item name="end_date" label="End Date"><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
          </div>
          <div className="grid grid-cols-3 gap-x-3">
            <Form.Item name="weekly_rate" label="Weekly Rate (£)" rules={[{ required: true }]}><InputNumber className="w-full" min={0} prefix="£" /></Form.Item>
            <Form.Item name="total_paid" label="Total Paid (£)"><InputNumber className="w-full" min={0} /></Form.Item>
            <Form.Item name="weeks_paid" label="Weeks Paid"><InputNumber className="w-full" min={0} step={0.5} /></Form.Item>
          </div>
          <Form.Item name="reclaim_amount" label="Reclaim Amount (£)"><InputNumber className="w-full" min={0} /></Form.Item>
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={create.isPending || upd.isPending}>Save</Button>
            <Button onClick={() => setModal({ open: false, rec: null })}>Cancel</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
